import type { GlobalAuditEntry } from '@/mocks/saGlobalAudit';
import { actionLabels, moduleLabels, moduleIcons, severityLabels } from '@/mocks/saGlobalAudit';

interface AuditTimelineProps {
  entries: GlobalAuditEntry[];
  onSelect: (entry: GlobalAuditEntry) => void;
}

const resultBadge: Record<string, string> = {
  success: 'bg-emerald-100 text-emerald-700',
  failure: 'bg-red-100 text-red-600',
  pending: 'bg-sky-100 text-sky-700',
};

const severityDot: Record<string, string> = {
  info: 'bg-foreground-300',
  warning: 'bg-amber-400',
  critical: 'bg-red-500',
};

const severityBorder: Record<string, string> = {
  info: 'border-l-foreground-300',
  warning: 'border-l-amber-400',
  critical: 'border-l-red-500',
};

function formatDate(dateStr: string): { date: string; time: string } {
  const parts = dateStr.split(' ');
  return { date: parts[0] ?? '', time: parts[1] ?? '' };
}

export default function AuditTimeline({ entries, onSelect }: AuditTimelineProps) {
  if (entries.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-16 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-timeline-view text-foreground-300 text-2xl"></i>
        </div>
        <p className="text-foreground-800 text-sm font-semibold mb-1">Sin entradas en la línea de tiempo</p>
        <p className="text-foreground-400 text-xs">No se encontraron registros con los filtros actuales.</p>
      </div>
    );
  }

  // Group entries by date
  const grouped = entries.reduce<Record<string, GlobalAuditEntry[]>>((acc, entry) => {
    const { date } = formatDate(entry.date);
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  const dateKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="px-5 py-3 border-b border-background-200 bg-background-100/50">
        <div className="flex items-center gap-2">
          <i className="ri-timeline-view text-foreground-500 text-sm"></i>
          <span className="text-foreground-800 text-xs font-semibold">Línea de Tiempo</span>
          <span className="text-foreground-400 text-[10px]">{entries.length} eventos</span>
        </div>
      </div>
      <div className="p-5 max-h-[600px] overflow-y-auto">
        {dateKeys.map((dateKey) => (
          <div key={dateKey} className="mb-6 last:mb-0">
            <div className="flex items-center gap-2 mb-3 sticky top-0 z-10 bg-background-50 py-1">
              <div className="w-2 h-2 rounded-full bg-foreground-400"></div>
              <p className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">{dateKey}</p>
              <span className="text-foreground-300 text-[9px]">{grouped[dateKey].length} eventos</span>
            </div>
            <div className="ml-1 space-y-0">
              {grouped[dateKey].map((entry, idx) => {
                const { time } = formatDate(entry.date);
                const isLast = idx === grouped[dateKey].length - 1;
                return (
                  <div key={entry.id} className="relative pl-6">
                    {/* Vertical line */}
                    {!isLast && (
                      <div className="absolute left-[7px] top-5 bottom-0 w-px bg-background-200"></div>
                    )}
                    {/* Dot */}
                    <div className={`absolute left-[3px] top-[10px] w-[9px] h-[9px] rounded-full ${severityDot[entry.severity]} ring-2 ring-background-50`}></div>
                    {/* Card */}
                    <button
                      onClick={() => onSelect(entry)}
                      className={`w-full text-left bg-background-50 border border-background-200 border-l-2 ${severityBorder[entry.severity]} rounded-xl p-3 mb-2 cursor-pointer hover:bg-background-100/60 transition-colors`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-foreground-400 text-[10px] font-mono whitespace-nowrap">{time}</span>
                          <span className="text-foreground-800 text-xs font-semibold truncate">{actionLabels[entry.action]}</span>
                        </div>
                        <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium ${resultBadge[entry.result]}`}>
                          {entry.result === 'success' ? 'OK' : entry.result === 'failure' ? 'FAIL' : 'PEND'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-foreground-600 text-[10px]">{entry.userName}</span>
                        <span className="text-foreground-300 text-[9px]">·</span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px]">
                          <i className={`${moduleIcons[entry.module]} text-[8px]`}></i>
                          {moduleLabels[entry.module]}
                        </span>
                        {entry.severity !== 'info' && (
                          <>
                            <span className="text-foreground-300 text-[9px]">·</span>
                            <span className={`text-[9px] font-medium ${entry.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`}>
                              {severityLabels[entry.severity]}
                            </span>
                          </>
                        )}
                      </div>
                      <p className="text-foreground-400 text-[10px] mt-1 truncate">{entry.details}</p>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}